import { useEffect, useState } from 'react';
import { Outlet, useMatches, Link } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { TopBar } from './TopBar';
import { MobileShell } from './MobileShell';
import { CommandPalette } from '@/components/CommandPalette';
import { xeroApi } from '@/api/xero';
import { toolsApi } from '@/api/tools';
import type { EntraSecretData } from '@/api/tools';
import { useIsAdmin } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/useIsMobile';
import { usePrefetchAppData } from '@/hooks/usePrefetchAppData';
import { AlertTriangle, Clock } from 'lucide-react';

/**
 * Top-level shell. Picks the desktop or mobile shell based on viewport width.
 * Each shell is a self-contained component so its hooks only run when active —
 * this keeps the desktop render path identical to the original implementation.
 */
export function AppShell() {
  const isMobile = useIsMobile();
  // Warm the main sections' data + code in the background after sign-in.
  usePrefetchAppData();

  // Browser tab shows the current page ("Jobs · Labyrinth") so tabs, history,
  // and bookmarks are tellable apart. Falls back to the plain app name.
  const matches = useMatches();
  const pageTitle = (matches[matches.length - 1]?.handle as any)?.title ?? '';
  useEffect(() => {
    document.title = pageTitle ? `${pageTitle} · Labyrinth Inventory` : 'Labyrinth Inventory';
  }, [pageTitle]);

  // Scrolling the page while a number input is focused silently changes its
  // value (native browser behaviour) — nasty in the quote editor where those
  // are prices and quantities. Blur the field so the wheel scrolls instead.
  useEffect(() => {
    function onWheel(e: WheelEvent) {
      const t = e.target;
      if (t instanceof HTMLInputElement && t.type === 'number' && document.activeElement === t) {
        t.blur();
      }
    }
    document.addEventListener('wheel', onWheel, { passive: true });
    return () => document.removeEventListener('wheel', onWheel);
  }, []);

  return (
    <>
      {isMobile ? <MobileShell /> : <DesktopShell />}
      {/* Cmd-K / Ctrl-K global search. Mounted once, listens on document.
          Stays mounted but renders nothing until the user opens it. */}
      <CommandPalette />
    </>
  );
}

// ── Desktop shell (original AppShell — unchanged) ─────────────────────────────
function DesktopShell() {
  const matches = useMatches();
  const title = (matches[matches.length - 1]?.handle as any)?.title ?? '';
  const isAdmin = useIsAdmin();
  const [entraWarning, setEntraWarning] = useState<EntraSecretData | null>(null);
  const [dismissed, setDismissed] = useState(false);

  // Fire-and-forget contact sync on app load. Backend honours a 10-minute cache
  // and will 200-skip if not due. Silently ignores errors (Xero not connected, etc.).
  useEffect(() => {
    xeroApi.autoSyncContacts().catch(() => {});
  }, []);

  // Check Entra secret expiry for directors/admins.
  useEffect(() => {
    if (!isAdmin) return;
    toolsApi.getEntraSecret()
      .then((data) => {
        if (data.configured && (data.status === 'warning' || data.status === 'expired')) {
          setEntraWarning(data);
        }
      })
      .catch(() => {}); // silently ignore if not configured or user lacks access
  }, [isAdmin]);

  // Admin-only: Manage goes to Admin -> System, and rotating the secret is an
  // admin action, so a director can only be nagged about something they can't do.
  const showBanner = isAdmin && entraWarning && !dismissed;

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <ImpersonationBanner />
        <TopBar title={title} />

        {showBanner && (
          <div style={{
            padding: '8px 24px',
            background: entraWarning.status === 'expired' ? '#fef2f2' : '#fffbeb',
            borderBottom: `2px solid ${entraWarning.status === 'expired' ? '#fca5a5' : '#fde68a'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 13,
            gap: 12,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {entraWarning.status === 'expired'
                ? <AlertTriangle size={16} style={{ color: '#c33', flexShrink: 0 }} />
                : <Clock size={16} style={{ color: '#92400e', flexShrink: 0 }} />}
              <span style={{ fontWeight: 600, color: entraWarning.status === 'expired' ? '#c33' : '#92400e' }}>
                {entraWarning.status === 'expired'
                  ? `Microsoft Entra secret expired ${Math.abs(entraWarning.daysUntilExpiry!)} day${Math.abs(entraWarning.daysUntilExpiry!) === 1 ? '' : 's'} ago`
                  : `Microsoft Entra secret expires in ${entraWarning.daysUntilExpiry} day${entraWarning.daysUntilExpiry === 1 ? '' : 's'}`}
              </span>
              <Link
                to="/admin/system"
                style={{
                  color: 'var(--brand)', fontWeight: 600, textDecoration: 'none',
                  marginLeft: 4, fontSize: 12,
                }}
              >
                Manage
              </Link>
            </div>
            <button
              onClick={() => setDismissed(true)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#999', fontSize: 16, padding: '0 4px', lineHeight: 1,
              }}
              title="Dismiss until next page load"
            >
              &times;
            </button>
          </div>
        )}

        <main style={{ flex: 1, padding: 24, overflowY: 'auto' }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
